import z from 'zod'
import { federalStates } from './enums/federal-states.schema'
import { gridOperators } from './enums/grid-operators.schema'
import { countrySchema } from './enums/countries.schema'

export const DUMMY_MALO_PREFIX = 'DUMMY' as const

function isMaloIdValid(value?: string): boolean {
  if (!value) return true
  if (!/^\d{11}$/.test(value)) return false
  if (!value.startsWith('1') && !value.startsWith('5')) return false
  let oddSum = 0
  let evenSum = 0
  for (let i = 0; i < 10; i++) {
    const d = Number(value[i])
    if (i % 2 === 0) oddSum += d
    else evenSum += d
  }
  const total = oddSum + evenSum * 2
  if (total === 0) return false
  const expectedCheck = (10 - (total % 10)) % 10
  return Number(value[10]) === expectedCheck
}

const maloIdSchema = z.string().optional().refine(isMaloIdValid, { message: 'Ungültige MaLo-ID' }).brand<'MaloId'>()
const maloIdWithPlaceholderSchema = z.union([
  maloIdSchema,
  z.string().regex(new RegExp(`^${DUMMY_MALO_PREFIX}\\d{6}$`)),
  z.null(),
])

export const YearlyPerformanceRowSchema = z.object({
  year: z.number().int().describe('Jahr'),
  usableCapacityTheoretical: z.number().min(0.1).max(50).describe('Nutzbare Kapazität theoretisch [MWh]'),
  usableCapacityActual: z.number().min(0.1).max(50).optional().describe('Nutzbare Kapazität aktuell [MWh]'),
  roundtripEfficiencyBatteryTheoretical: z
    .number()
    .int()
    .min(70)
    .max(100)
    .meta({ description: 'Roundtrip-Wirkungsgrad theoretisch', suffix: '%' }),
  roundtripEfficiencyBatteryActual: z
    .number()
    .int()
    .min(70)
    .max(100)
    .optional()
    .meta({ description: 'Roundtrip-Wirkungsgrad aktuell', suffix: '%' }),
})

export const YearlyPerformanceListSchema = z.array(YearlyPerformanceRowSchema).max(10)

/** Fields rendered by MagicFormComponent — excludes array fields that magic-form cannot render. */
const BatteryAssetBaseSchema = z.object({
  apcId: z.string().optional().describe('APC-ID'),
  piId: z.string().optional().describe('PI-ID'),
  gridOperatorConfirmationDate: z.coerce.date().optional().describe('Bestätigungsdatum Netzbetreiber'),
  commissioningDatePlanned: z.coerce.date().meta({
    description: 'Geplantes Inbetriebnahmedatum',
    warning: (value: unknown) => {
      if (!value) return null
      const date = value instanceof Date ? value : new Date(value as string)
      if (Number.isNaN(date.getTime())) return null
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return date < today ? 'Datum liegt in der Vergangenheit' : null
    },
  }),
  commissioningDate: z.coerce.date().optional().describe('Tatsächliches Inbetriebnahmedatum'),
  decommissioningDate: z.coerce.date().optional().describe('Außerbetriebnahmedatum'),
  controlZone: z
    .enum(['50Hertz Transmission GmbH', 'Amprion GmbH', 'TenneT TSO GmbH', 'TransnetBW GmbH'])
    .describe('Regelzone'),
  gridOperator: z.enum(gridOperators.map((o) => o.BDEWCode)).meta({
    description: 'Netzbetreiber',
    widget: 'autocomplete',
    labels: gridOperators.map((o) => `${o.UID} / ${o.BDEWCode} / ${o.name}`),
  }),
  mastrId: z
    .string()
    .regex(/^SEE\d{12}$/, 'MaStR-ID muss mit "SEE" gefolgt von 12 Ziffern bestehen')
    .describe('MaStR-Nummer der Einheit (SEE-Nr.)'),
  sseNumber: z
    .string()
    .regex(/^SSE\d{12}$/, 'MaStR-ID muss mit "SSE" gefolgt von 12 Ziffern bestehen')
    .describe('MaStR-Nummer des Stromspeichers (SSE-Nr.)'),
  balancingGroup: z
    .enum(['11XMPM-ENBW----B', '11Y0-0000-0455-E', '11XENBW-PROD---Q'])
    .optional()
    .meta({
      description: 'Bilanzkreis',
      labels: ['geförderte DV', 'sonstige DV', 'Graustromspeicher'],
    }),
  connectivityProviderLocation: z
    .enum(['EnBW (Harlei)'])
    .default('EnBW (Harlei)')
    .describe('Konnektivitätsdienstleister (Lage)'),
  connectivityProviderMzp: z
    .enum(['EnBW (Harlei)'])
    .default('EnBW (Harlei)')
    .describe('Konnektivitätsdienstleister (MZP)'),
  gridLinkMax: z.number().min(0.1).describe('Max. Netzanschlussleistung [MW]'),
  installedPowerKw: z.number().min(0.1).max(1000).describe('Installierte Leistung Einspeisung [MW]'),
  installedPowerDemandKw: z.number().min(0.1).max(1000).describe('Installierte Leistung Bezug [MW]'),
  overallSystemCoolingConcept: z.enum(['luftgekühlt', 'flüssigkeitsgekühlt']).describe('Kühlkonzept der Gesamtanlage'),
  maxActivePowerGradientPercentSec: z.number().describe('Maximaler Wirkleistungsgradient [%P_inst/s]'),
  plantCertificateTypeAAvailableAtCommissioning: z
    .boolean()
    .default(false)
    .describe(
      'Mit Herstellung der Betriebsbereitschaft des Speichers durch den Anlagenbetreiber liegt Anlagenzertifikat - Typ A vor'
    ),
  otherRestrictionsInOperatingMode: z.string().optional().describe('Sonstige Restriktionen in der Fahrweise'),
  gridOperatorCaseId: z.string().optional().describe('Vorgangs-ID Netzbetreiber'),
  maloId: maloIdWithPlaceholderSchema.describe('MaLo-ID Einspeisung'),
  maloIdDemand: maloIdSchema.optional().describe('MaLo-ID Bezug'),
  maloIdSelfConsumptionBattery: maloIdSchema.optional().describe('MaLo-ID Eigenverbrauch Batterie'),
  country: countrySchema.describe('Land').describe('Land'),
  federalState: z.enum(federalStates).describe('Bundesland'),
  postalCode: z.string().length(5).describe('PLZ'),
  city: z.string().describe('Stadt'),
  street: z.string().describe('Straße'),
  streetNumber: z.string().describe('Hausnummer'),
  latitude: z.number().describe('Breitengrad'),
  longitude: z.number().describe('Längengrad'),
  trId: z.string().optional().describe('TR-ID (RD 2.0)'),
  srId: z
    .string()
    .regex(/^C[A-Za-z0-9]{10}$/)
    .optional()
    .describe('SR-ID (RD 2.0)'),
  unavailabilityPortalUsers: z.email().optional().describe('Nutzer Nichtverfügbarkeitenportal (Mailadresse)'),
  clusterId: z.string().optional().describe('Cluster'),
  locationId: z.string().optional().describe('Location ID'),
  zaehlpunktIdIntern: z.string().optional().describe('Zählpunkt ID Intern'),
  rdEiv: z.string().optional().describe('RD EIV'),
  lieferant: z.string().optional().describe('Lieferant'),
  rdBtr: z.string().optional().describe('RD BTR'),
})

const withMaloCaseRule = <T extends z.ZodObject>(schema: T) =>
  schema
    .superRefine(({ maloId, gridOperatorCaseId }, ctx) => {
      if (
        (maloId == null || maloId === '' || (maloId as string).startsWith(DUMMY_MALO_PREFIX)) &&
        !gridOperatorCaseId
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['gridOperatorCaseId'],
          message: 'Vorgangs-ID Netzbetreiber ist erforderlich, wenn keine gültige MaLo-ID vorhanden ist',
        })
      }
    })
    .describe('Anlagendaten')

export const BatteryAssetFormSchema = withMaloCaseRule(BatteryAssetBaseSchema)

/** Full persistence schema — extends the form schema with the yearly-performance array. */
export const BatteryAssetSchema = withMaloCaseRule(
  BatteryAssetBaseSchema.extend({
    yearlyPerformance: YearlyPerformanceListSchema.optional().describe('Jährliche Kennzahlen'),
  })
).describe('Anlagendaten')

export const BatteryAssetSchemaForSharepoint = BatteryAssetBaseSchema.omit({ gridOperator: true })
  .partial()
  .extend({
    contractNumber: z.string().min(1),
    productionTechnology: z.enum(['battery']),
    gridOperatorUid: z.string().optional(),
    usableCapacityTheoretical: z.number().nullish(),
    usableCapacityActual: z.number().nullish(),
    roundtripEfficiencyBatteryTheoretical: z.number().nullish(),
    roundtripEfficiencyBatteryActual: z.number().nullish(),
    trValidFrom: z.coerce.date(),
    trValidUntil: z.coerce.date(),
  })
  .transform(
    ({
      gridOperatorUid,
      roundtripEfficiencyBatteryTheoretical,
      roundtripEfficiencyBatteryActual,
      gridLinkMax,
      installedPowerKw,
      installedPowerDemandKw,
      usableCapacityTheoretical,
      usableCapacityActual,
      maxActivePowerGradientPercentSec,
      ...rest
    }) => {
      const percentToFraction = (v: number | null | undefined) => (v == null ? v : v / 100)
      const mwToKw = (v: number | null | undefined) => (v == null ? v : v * 1000)
      const installedPower = Math.min(
        Math.max(installedPowerKw ?? 0, installedPowerDemandKw ?? 0),
        gridLinkMax ?? Number.POSITIVE_INFINITY
      )
      const operator = gridOperatorUid ? gridOperators.find((o) => o.BDEWCode === gridOperatorUid) : undefined
      return {
        ...rest,
        gridLinkMax: mwToKw(gridLinkMax),
        installedPowerKw: mwToKw(installedPowerKw),
        installedPowerDemandKw: mwToKw(installedPowerDemandKw),
        usableCapacityTheoretical: mwToKw(usableCapacityTheoretical),
        usableCapacityActual: mwToKw(usableCapacityActual),
        maxActivePowerGradientMwSec:
          maxActivePowerGradientPercentSec == null
            ? maxActivePowerGradientPercentSec
            : (maxActivePowerGradientPercentSec * installedPower) / 100,
        gridOperator: operator?.name,
        gridOperatorUidCode: operator?.BDEWCode ?? gridOperatorUid,
        roundtripEfficiencyBatteryTheoretical: percentToFraction(roundtripEfficiencyBatteryTheoretical),
        roundtripEfficiencyBatteryActual: percentToFraction(roundtripEfficiencyBatteryActual),
      }
    }
  )
